import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, concatMap, map, retry, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { WindowLoaderService } from 'src/app/shared/components/window-loader/window-loader.service';
import { ApiAccess } from '../../../core/models/ApiAccess';
import { RequestBuilder } from '../../../core/models/request-builder';

const REFRESH_INTERVAL = 3600000; //1 hour to reloading the token the token is valid for 1 day.......

@Injectable({
  providedIn: 'root'
})
export class BaseService {

  requestBuilder = new RequestBuilder();
  cachedApiAcces$: Observable<ApiAccess>;
  unsubscribe = new Subject();

  constructor(private httpClient: HttpClient, private loaderService: WindowLoaderService) {
  }

  private getAnonymousAPIAccess = new Observable<any>(observer => {
    return this.httpClient.get<any>('/api/AnonymousAPIAccess').pipe(shareReplay(50)).subscribe(
      (res) => {
        this.requestBuilder.serviceUrl = res['apiEndPoint'];
        observer.next(this.requestBuilder);
      },
      (error) => { observer.error(error); });
  });

  private requestApiAccess() {
    return this.httpClient.get<ApiAccess>('/api/GetAPIAccess');
  }

  private getAccessTokenCached(): Observable<ApiAccess> {
    if (!this.cachedApiAcces$) {
      const timer$ = timer(0, REFRESH_INTERVAL);

      this.cachedApiAcces$ = timer$.pipe(
        switchMap(_ => this.requestApiAccess()),
        shareReplay(50)
      );

    }

    return this.cachedApiAcces$;

  }

  private getAccessToken = new Observable<RequestBuilder>(observer => {
    return this.getAccessTokenCached().subscribe(
      (res) => {
        const accessToken = res.accessToken;
        this.requestBuilder.serviceUrl = res.apiEndPoint;

        if (accessToken != '') {
          this.requestBuilder.headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` });
          this.requestBuilder.formHeaders = new HttpHeaders({ 'Authorization': `Bearer ${accessToken}` });
        }
        else {
          console.log('No access token provided.');
          observer.error('No access token provided.');
          observer.complete();
          return;
        }

        observer.next(this.requestBuilder);
        observer.complete();
      },
      (error) => {
        observer.error(error);
        observer.complete();
      });
  });

  getLocal<T>(url: string): Observable<T> {
    return this.httpClient.get<T>(url);
  }
  onDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  get<T>(url: string): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.get<T>(`${ret.serviceUrl}${url}`, { headers: ret.headers }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              if (url == 'User/GetUserDetails') {
                window.location.href = `/Session/SignOut`;
              }
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }

  getBlob(url: string): Observable<Blob> {
    const obs = new Observable<Blob>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.get(`${ret.serviceUrl}${url}`, { headers: ret.headers, responseType: "blob" }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }
  postBlob(url: string, data: any): Observable<Blob> {
    return new Observable<Blob>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.post(`${ret.serviceUrl}${url}`, data, { headers: ret.headers, responseType: "blob" }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            }
          );
        },
        (error) => {
          observer.error(error);
          observer.complete();
        }
      );
    });
  }
  

  getWithLoader<T>(url: string): Observable<T> {
    return this.loaderService.blockWithLoader(this.get(url));
  }

  anonymousGet<T>(url: string): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAnonymousAPIAccess.subscribe(
        (ret) => {
          this.httpClient.get<T>(`${ret.serviceUrl}${url}`).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }

  post<T>(url: string, data: any): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.post<T>(`${ret.serviceUrl}${url}`, data, { headers: ret.headers }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        }
      );
    });

    return obs;
  }

  // fileUpload<T>(url: string, data: any): Observable<T> {
  //   return new Observable<T>(observer => {
  //     this.getAccessToken.subscribe(
  //       (requestBuilder) => {
  //         // Extract the Authorization token from the headers
  //         const authorizationHeader = requestBuilder.headers.get('Authorization');
  //         console.log('Authorization Token:', authorizationHeader);

  //         // Create headers using the extracted token
  //         const headers = new HttpHeaders({
  //           'Authorization': authorizationHeader || '', // Fallback to empty if undefined
  //         });

  //         // Use the extracted headers to make the POST request
  //         this.httpClient.post<T>(`${requestBuilder.serviceUrl}${url}`, data, { headers }).subscribe(
  //           (response) => {
  //             observer.next(response);
  //             observer.complete();
  //           },
  //           (error) => {
  //             observer.error(error);
  //             observer.complete();
  //           }
  //         );
  //       },
  //       (error) => {
  //         observer.error(error);
  //         observer.complete();
  //       }
  //     );
  //   });
  // }
  fileUpload<T>(url: string, data: any): Observable<T> {
    return new Observable<T>((observer) => {
      this.getAccessToken.subscribe(
        (requestBuilder) => {
          // Extract the Authorization token from the headers
          const authorizationHeader = requestBuilder.headers.get('Authorization');
          console.log('Authorization Token:', authorizationHeader);
  
          // Create headers using the extracted token
          const headers = new HttpHeaders({
            'Authorization': authorizationHeader || '', // Fallback to empty if undefined
          });
  
          // Use the extracted headers to make the POST request
          this.httpClient.post<T>(`${requestBuilder.serviceUrl}${url}`, data, { headers }).subscribe(
            (response) => {
              observer.next(response); // Pass the response to the observer
              observer.complete(); // Complete the observable
            },
            (error) => {
              observer.error(error); // Pass the error to the observer
              observer.complete(); // Complete the observable
            }
          );
        },
        (error) => {
          observer.error(error); // Handle token retrieval errors
          observer.complete();
        }
      );
    });
  }
  

  postAndGetBlob(url: string, data: any): Observable<Blob> {
    const obs = new Observable<Blob>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.post(`${ret.serviceUrl}${url}`, data, { headers: ret.headers, responseType: "blob" }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        }
      );
    });

    return obs;
  }
  postWithApiEndpoint<T>(fullServiceUrl: string, data: any, apiAccessKey: string): Observable<T> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'x-api-key': apiAccessKey });
    const obs = new Observable<T>(observer => {
      this.httpClient.post<T>(`${fullServiceUrl}`, data, { headers: headers }).subscribe(
        (response) => {
          observer.next(response);
          observer.complete();
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });
    return obs;
  }


  put<T>(url: string, data: any): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.put<T>(`${ret.serviceUrl}${url}`, data, { headers: ret.headers }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        }
      );
    });

    return obs;
  }

  patch<T>(url: string, data: any): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.patch<T>(`${ret.serviceUrl}${url}`, data, { headers: ret.headers }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }

  anonymousPost<T>(url: string, data: any): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAnonymousAPIAccess.subscribe(
        (ret) => {
          this.httpClient.post<T>(`${ret.serviceUrl}${url}`, data, { headers: ret.headers }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }

  delete<T>(url: string): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.delete<T>(`${ret.serviceUrl}${url}`, { headers: ret.headers }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }

  upload<T>(url: string, data: FormData): Observable<T> {
    const obs = new Observable<T>(observer => {
      this.getAccessToken.subscribe(
        (ret) => {
          this.httpClient.post<T>(`${ret.serviceUrl}${url}`, data, { headers: ret.formHeaders }).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            });
        },
        (error) => {
          observer.error(error);
          observer.complete();
        });
    });

    return obs;
  }
  uploadwithprogress(url: string, data: FormData, progress: number): Observable<any> {
    return this.getAccessToken
      .pipe(
        takeUntil(this.unsubscribe),
        concatMap((ret) => {
          const req = new HttpRequest('POST', `${ret.serviceUrl}${url}`, data, {
            headers: ret.formHeaders,
            reportProgress: true,
            responseType: 'json'
          });
          return this.httpClient.request(req);
        }),
        retry(1)
      );
  }
}

